// Rapor Tutarlılık sekmesi — bant + filtreler + bulgu listesi + eylemler + geçmiş + denetim başlatma.
import { useEffect, useMemo, useState } from "react";
import {
  FINDING_KINDS,
  FINDING_STATUSES,
  type FindingStatus,
  type ReportCheckDetail,
  type ReportCheckFindingDto,
} from "@mahir/shared";
import { getReportCheck } from "../../api/report-check";
import { fillTemplate } from "../../lib/period-ui";
import { REPORT_CHECK_COPY, reportCheckErrorText } from "../../lib/report-check-copy";
import { useReportCheckStore } from "../../stores/report-check-store";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ReportCheckBand } from "./ReportCheckBand";

interface Props {
  projectId: string;
  onOpenSection: (code: string, excerpt?: string) => void;
}
const C = REPORT_CHECK_COPY;
const SEL = "px-2 py-1 rounded-md text-xs";
const SEL_STYLE = { border: "1px solid var(--color-border)", background: "var(--color-surface)" };

export interface FindingFilters {
  kind: string;
  severity: string;
  confidence: string;
  status: string;
  section: string;
}

/** Boş filtre değeri = "tümü". */
export function filterFindings(
  findings: ReportCheckFindingDto[],
  f: FindingFilters,
): ReportCheckFindingDto[] {
  return findings.filter(
    (x) =>
      (!f.kind || x.kind === f.kind) &&
      (!f.severity || x.severity === f.severity) &&
      (!f.confidence || x.confidence === f.confidence) &&
      (!f.status || x.status === f.status) &&
      (!f.section || x.sectionCodes.includes(f.section)),
  );
}

export function ReportCheckTab({ projectId, onOpenSection }: Props): JSX.Element {
  const store = useReportCheckStore();
  const { current, history, estimate, busy, error } = store;
  const [viewing, setViewing] = useState<ReportCheckDetail | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [kind, setKind] = useState("");
  const [severity, setSeverity] = useState("");
  const [confidence, setConfidence] = useState("");
  const [status, setStatus] = useState<string>("open"); // varsayılan: açık
  const [section, setSection] = useState("");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const active = current?.status === "queued" || current?.status === "running";
  // Yeni koşum başlatıldı ama `current` hâlâ önceki (birazdan değiştirilecek) koşum: bant
  // "Denetleniyor" der, bulgular salt okunur — eski koşuma yazılan durum yeni koşumla kaybolurdu.
  const replacing = !active && (busy === "starting" || busy === "polling");
  const shown = viewing ?? current;
  const readOnly = viewing !== null || shown?.supersededBy != null || replacing;
  // Başarısız son koşum (history createdAt desc) güncelden yeniyse üstte görünür bant (I2).
  const latest = history[0];
  const latestFailed =
    latest?.status === "failed" && (!current || current.createdAt < latest.createdAt)
      ? latest
      : null;

  // Yeni koşum bitince (current değişince) geçmiş görünümünden çık.
  useEffect(() => setViewing(null), [current?.id]);

  const sectionCodes = useMemo(
    () => [...new Set((shown?.findings ?? []).flatMap((f) => f.sectionCodes))].sort(),
    [shown],
  );
  const rows = filterFindings(shown?.findings ?? [], {
    kind,
    severity,
    confidence,
    status,
    section,
  });

  const lvl = (Object.keys(C.levels) as (keyof typeof C.levels)[]).map(
    (k) => [k, C.levels[k]] as [string, string],
  );
  const filterSelects: {
    label: string;
    value: string;
    set: (v: string) => void;
    options: [string, string][];
  }[] = [
    {
      label: C.filters.kind,
      value: kind,
      set: setKind,
      options: FINDING_KINDS.map((k) => [k, C.kinds[k]]),
    },
    { label: C.filters.severity, value: severity, set: setSeverity, options: lvl },
    { label: C.filters.confidence, value: confidence, set: setConfidence, options: lvl },
    {
      label: C.filters.status,
      value: status,
      set: setStatus,
      options: FINDING_STATUSES.map((k) => [k, C.findingStatus[k]]),
    },
    {
      label: C.filters.section,
      value: section,
      set: setSection,
      options: sectionCodes.map((c) => [c, c]),
    },
  ];

  // Her tıklamada güncel tahmin; diyalog yalnız hatasız + sığan raporda açılır.
  const openConfirm = async (): Promise<void> => {
    await store.refreshEstimate(projectId);
    const st = useReportCheckStore.getState();
    if (st.error === null && st.estimate?.limit.fits === true) setConfirmOpen(true);
  };
  const confirm = async (): Promise<void> => {
    setConfirmOpen(false);
    await store.start(projectId);
  };
  const setFinding = (f: ReportCheckFindingDto, next: FindingStatus): void => {
    const note = next === "dismissed" ? (notes[f.id]?.trim() ?? "") || undefined : undefined;
    void store.setFindingStatus(projectId, f.checkId, f.id, next, note);
  };
  const viewRun = async (id: string): Promise<void> => {
    if (id === current?.id) return setViewing(null);
    try {
      setViewing(await getReportCheck(projectId, id));
    } catch {
      setViewing(null); // okunamayan geçmiş koşum — güncel görünüme dön
    }
  };

  // Tahmini süre: bölüm başına ~1 sn varsayımıyla dakika = max(1, round(bölüm/60)).
  const body = estimate
    ? fillTemplate(C.confirm.body, {
        sections: estimate.sectionCount,
        tokens: Math.round(estimate.promptTokens / 1000),
        cost: estimate.estimatedCostUsd.toFixed(2),
        minutes: Math.max(1, Math.round(estimate.sectionCount / 60)),
      })
    : "";
  const tooLarge = estimate?.limit.fits === false;

  return (
    <div data-testid="report-check-tab">
      <div className="mb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void openConfirm()}
          disabled={busy !== "idle" || active}
          className="px-3 py-1.5 rounded-md text-xs disabled:opacity-50"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            fontWeight: 600,
          }}
        >
          {active
            ? C.status.running
            : busy === "idle"
              ? C.startButton
              : busy === "estimating"
                ? C.estimating
                : C.status.running}
        </button>
        {tooLarge && (
          <span className="text-xs" style={{ color: "var(--color-error)" }}>
            {C.errors.report_too_large}
          </span>
        )}
      </div>
      {error && (
        <p role="alert" className="mb-3 text-xs" style={{ color: "var(--color-error)" }}>
          {error}
        </p>
      )}
      {latestFailed && (
        <p
          role="alert"
          data-testid="report-check-failed"
          className="mb-3 rounded-md px-3 py-2 text-xs"
          style={{ color: "var(--color-error)", border: "1px solid var(--color-border)" }}
        >
          {C.status.failed} · {reportCheckErrorText(latestFailed.failureReason)}
        </p>
      )}
      {!shown ? (
        <p className="text-sm" style={{ color: "var(--color-text-3)" }}>
          {C.empty}
        </p>
      ) : (
        <>
          <ReportCheckBand
            check={replacing && !viewing ? { ...shown, status: "running" } : shown}
          />
          {shown.supersededBy != null && (
            <p className="mb-2 text-xs" style={{ color: "var(--color-text-3)" }}>
              {C.history.superseded}
            </p>
          )}
          <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label={C.filters.group}>
            {filterSelects.map((f) => (
              <select
                key={f.label}
                className={SEL}
                style={SEL_STYLE}
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                aria-label={f.label}
              >
                <option value="">{fillTemplate(C.filters.all, { label: f.label })}</option>
                {f.options.map(([v, text]) => (
                  <option key={v} value={v}>
                    {text}
                  </option>
                ))}
              </select>
            ))}
          </div>
          <ul>
            {rows.map((f) => (
              <li
                key={f.id}
                className="mb-3 rounded-md p-3 text-sm"
                data-testid="report-check-finding"
                style={{
                  border: "1px solid var(--color-border)",
                  background: "var(--color-surface)",
                }}
              >
                <div className="text-xs mb-1" style={{ color: "var(--color-text-3)" }}>
                  {C.kinds[f.kind]} · {C.filters.severity}: {C.levels[f.severity]} ·{" "}
                  {C.filters.confidence}: {C.levels[f.confidence]} · {C.findingStatus[f.status]}
                  {!f.evidenceComplete && ` · ${C.evidenceIncomplete}`}
                </div>
                <strong>{f.title}</strong>
                <p className="mt-1 text-xs">{f.explanation}</p>
                {f.suggestion && (
                  <p className="mt-1 text-xs" style={{ color: "var(--color-text-2)" }}>
                    {f.suggestion}
                  </p>
                )}
                <ul className="mt-2">
                  {f.quotes.map((q, i) => (
                    <li key={`${q.sectionCode}-${i.toString()}`} className="text-xs mb-1">
                      <button
                        type="button"
                        onClick={() =>
                          onOpenSection(q.sectionCode, q.verified ? q.excerpt : undefined)
                        }
                        style={{ color: "var(--color-primary)", textAlign: "left" }}
                      >
                        {q.sectionCode}: “{q.excerpt}”
                      </button>
                      {!q.verified && (
                        <span style={{ color: "var(--color-warning-text)" }}>
                          {" "}
                          · {C.quoteUnverified}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {!readOnly && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {f.status === "open" ? (
                      <>
                        <button
                          type="button"
                          className={SEL}
                          style={SEL_STYLE}
                          onClick={() => setFinding(f, "resolved")}
                        >
                          {C.actions.resolve}
                        </button>
                        <input
                          className={SEL}
                          style={SEL_STYLE}
                          aria-label={C.actions.noteLabel}
                          placeholder={C.actions.noteLabel}
                          value={notes[f.id] ?? ""}
                          onChange={(e) => setNotes({ ...notes, [f.id]: e.target.value })}
                        />
                        <button
                          type="button"
                          className={SEL}
                          style={SEL_STYLE}
                          onClick={() => setFinding(f, "dismissed")}
                        >
                          {C.actions.dismiss}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={SEL}
                        style={SEL_STYLE}
                        onClick={() => setFinding(f, "open")}
                      >
                        {C.actions.reopen}
                      </button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {history.length >= 1 && (
        <section className="mt-4">
          <h3
            className="mb-2 text-xs uppercase tracking-wider"
            style={{ color: "var(--color-text-3)" }}
          >
            {C.history.title}
          </h3>
          <ul className="text-xs">
            {history.map((h) => (
              <li key={h.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => void viewRun(h.id)}
                  style={{ color: "var(--color-primary)" }}
                >
                  v{h.version} · {new Date(h.createdAt).toLocaleString("tr-TR")} ·{" "}
                  {C.status[h.status]}
                  {h.supersededBy != null ? ` · ${C.history.superseded}` : ""}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <ConfirmDialog
        open={confirmOpen && estimate !== null && !tooLarge}
        title={C.confirm.title}
        body={body}
        confirmLabel={C.confirm.confirmLabel}
        cancelLabel={C.confirm.cancelLabel}
        tone="neutral"
        onConfirm={() => void confirm()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
